/**
 * First-time guide popup (screens 3a/3b): 560px modal shown on the first
 * visit to Step 2 only (persisted via the store's markGuideSeen →
 * localStorage `rpl_guide_seen`). Four slides whose 280px warm-grey graphic
 * area is composed from the UI's own elements in CSS — no image assets.
 * Navigation: dots, arrow keys, swipe; ✕ / Skip / final "Start ordering"
 * all dismiss permanently.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ModalShell } from '../sizepaper/ModalShell';
import './guide.css';

interface Slide {
  title: string;
  body: ReactNode;
  graphic: ReactNode;
}

/* ---------- slide graphics (CSS compositions of real UI elements) ---------- */

function CardsGraphic() {
  return (
    <div className="gg gg--cards">
      <div className="gg__card gg__card--tilt-l gg__card--sel">
        <div className="gg__photo gg__photo--a">
          <span className="gg__tick">✓</span>
        </div>
        <span className="gg__line" />
      </div>
      <div className="gg__card gg__card--lift gg__card--sel">
        <div className="gg__photo gg__photo--b">
          <span className="gg__tick">✓</span>
        </div>
        <span className="gg__line" />
      </div>
      <div className="gg__card gg__card--tilt-r gg__card--dim">
        <div className="gg__photo gg__photo--c">
          <span className="gg__tick gg__tick--off" />
        </div>
        <span className="gg__line" />
      </div>
      <span className="gg__hand" aria-hidden>
        👆
      </span>
    </div>
  );
}

function SizeGraphic() {
  return (
    <div className="gg gg--stack">
      <span className="gg__dd">4 × 6&quot; · $0.70 ▾</span>
      <span className="gg__seg">
        <span className="gg__seg-on">Gloss</span>
        <span>Lustre</span>
      </span>
    </div>
  );
}

function AddGraphic() {
  return (
    <div className="gg gg--stack">
      <span className="gg__gold">Add prints</span>
      <span className="gg__chip">4×6 (2) Gloss ✓</span>
    </div>
  );
}

function CheckGraphic() {
  return (
    <div className="gg gg--badges">
      <span className="gg__badge gg__badge--ok">✓ Fits</span>
      <span className="gg__badge gg__badge--warn">Auto-cropped</span>
      <span className="gg__badge gg__badge--err">✕ Too low</span>
    </div>
  );
}

const SLIDES: Slide[] = [
  {
    title: 'Tap photos to select them',
    body: (
      <>
        Tap one, a few, or hit <strong>Select all</strong>. Whatever you choose next applies to
        every selected photo at once.
      </>
    ),
    graphic: <CardsGraphic />,
  },
  {
    title: 'Pick a size & paper',
    body: <>Prices show as you choose. Gloss shines, Lustre resists glare.</>,
    graphic: <SizeGraphic />,
  },
  {
    title: 'Add prints',
    body: <>One tap adds that size to every selected photo. Repeat for more sizes.</>,
    graphic: <AddGraphic />,
  },
  {
    title: 'We check every photo',
    body: <>We check sizing and quality automatically. We only flag what needs you.</>,
    graphic: <CheckGraphic />,
  },
];

export interface GuideModalProps {
  onDone: () => void;
}

export function GuideModal({ onDone }: GuideModalProps) {
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const last = index === SLIDES.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(SLIDES.length - 1, i + 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const slide = SLIDES[index];

  return (
    <ModalShell onClose={onDone} width={560} dim={0.88} label="How ordering works" className="guide">
      <div
        className="guide__graphic"
        onTouchStart={(e) => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchX.current;
          touchX.current = null;
          const end = e.changedTouches[0]?.clientX;
          if (start == null || end == null) return;
          const dx = end - start;
          if (dx < -40) setIndex((i) => Math.min(SLIDES.length - 1, i + 1));
          else if (dx > 40) setIndex((i) => Math.max(0, i - 1));
        }}
      >
        <button type="button" className="guide__close" aria-label="Close guide" onClick={onDone}>
          ✕
        </button>
        {slide.graphic}
      </div>

      <div className="guide__body">
        <span className="guide__eyebrow">How ordering works · {index + 1} of {SLIDES.length}</span>
        <h2 className="guide__title">{slide.title}</h2>
        <p className="guide__copy">{slide.body}</p>
        <div className="guide__dots" role="tablist" aria-label="Guide slides">
          {SLIDES.map((s, i) => (
            <button
              key={s.title}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Slide ${i + 1}`}
              className={`guide__dot${i === index ? ' guide__dot--on' : ''}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
        <div className="guide__actions">
          <button type="button" className="guide__skip" onClick={onDone}>
            Skip the tour
          </button>
          <button
            type="button"
            className="guide__next"
            onClick={() => (last ? onDone() : setIndex((i) => i + 1))}
          >
            {last ? 'Start ordering' : 'Next →'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
